import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import SvgIcon from '@mui/material/SvgIcon';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ReactComponent as SidebarCrewMgmtIcon } from '../assets/icons/sidebar_crew_management.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import RankStar from '../components/RankStar';
import { crewApi, applicationApi, adminWorkerApi, offersApi, getApiErrorMessage } from '../services/api';
import type {
  AdminWorkerCreatePayload,
  AdminWorkerListItem,
  StoreWorkerSokyuStatusItem,
} from '../services/api';
import { CrewRanksSummary, JobApplication } from '../types';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import { getRankTierLabel } from '../constants/rankColors';
import { useCrewRankTiers } from '../hooks/useCrewRankTiers';
import { isOldEnough, UNDER_AGE_MESSAGE } from '../utils/age';

const PRIMARY_BLUE = '#0071e3';
const ADVICE_BADGE_PURPLE = '#673FC8';
const ADVICE_BADGE_GRADIENT = 'linear-gradient(90deg, #7e318e 0%, #534dff 100%)';
// 課題125: 返信済み（承諾/見送り）特別オファーのオレンジ通知バッジ
const OFFER_REPLY_BADGE_ORANGE = '#e07b1a';

// クルー一覧の1行（store_user 単位に集約した表示用）
interface CrewListItem {
  storeUserId: string;
  name: string;
  tierCode?: string | null;
  activeAdviceCount?: number;
  // 返信済み（承諾/見送り）の特別オファー / 招待状件数。一覧のオレンジ通知バッジ用
  repliedOfferCount?: number;
  // 管理画面登録（即払い非利用）ワーカーの表示用フラグ
  isAdminRegistered?: boolean;
  lineLinked?: boolean;
  // 「応募がある＝採用」の運用に合わせ、未採用（応募中/審査中）の応募者もクルー一覧に載せる。
  // 未採用者は rank 未導出で詳細バンドルが 404 になるため、詳細遷移は不可にしステータスを表示する。
  applicationStatus?: JobApplication['status'];
  isPendingApplicant?: boolean;
}

// クルー一覧に載せる応募ステータス。
// Re:Crew は「店舗がまた働いてほしい人に登録を促す」運用のため 応募がある＝採用 とみなし、
// 明示的に不成立となった rejected / cancelled / no_show を除く応募をすべてクルーとして表示する。
// （旧仕様は accepted のみ＝店舗が採用操作をしないと一覧に出ない問題があった）
const CREW_APPLICATION_STATUSES: readonly string[] = ['submitted', 'under_review', 'accepted'];

// 未採用（応募中/審査中）応募者の行に表示するステータスラベル
const PENDING_STATUS_LABEL: Record<string, string> = {
  submitted: '応募中',
  under_review: '審査中',
};

// 新規登録ダイアログのフォーム初期値
const EMPTY_WORKER_FORM = {
  last_name: '',
  first_name: '',
  birth_date: '',
  phone: '',
  email: '',
  memo: '',
  // 飛び込み出勤（シフト未応募QR出退勤）に自動適用する既定時給（円）。空文字＝未設定。
  default_hourly_wage: '',
};

const CrewManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { storeId } = useAuth();
  const { managementToken } = useManagementAuth();
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [summary, setSummary] = useState<CrewRanksSummary | null>(null);
  const [crews, setCrews] = useState<CrewListItem[]>([]);
  const [adminWorkers, setAdminWorkers] = useState<AdminWorkerListItem[]>([]);

  // ─── 即払い利用状態（store_user_id → 状態）と切替ダイアログ ────────────
  // docs/即給利用切替_設計書.md §5。応募ベースの一覧は user_id / sokyu_opt_out を
  // 持たないため、店舗全ワーカー分を別APIで取得してマージする。
  const [sokyuStatusMap, setSokyuStatusMap] = useState<Map<string, StoreWorkerSokyuStatusItem>>(new Map());
  const [toggleTarget, setToggleTarget] = useState<{
    status: StoreWorkerSokyuStatusItem;
    nextOptOut: boolean;
  } | null>(null);
  const [isToggling, setIsToggling] = useState<boolean>(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [toggleSuccess, setToggleSuccess] = useState<string | null>(null);

  // ─── 未連携の事前登録クルーの削除（③で二重登録になった孤児行の掃除） ─────
  // docs/ワーカー自己登録_名寄せ3段階_設計書.md §4。本人が別の情報で自己登録すると
  // 新規クルーとして作成され、事前登録行が未連携のまま残るためそれを削除する。
  const [deleteTarget, setDeleteTarget] = useState<CrewListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);

  // ─── 既定時給の編集（飛び込み出勤に自動適用される時給） ─────────────────
  const [wageTarget, setWageTarget] = useState<StoreWorkerSokyuStatusItem | null>(null);
  const [wageInput, setWageInput] = useState<string>('');
  const [isSavingWage, setIsSavingWage] = useState<boolean>(false);
  const [wageError, setWageError] = useState<string | null>(null);

  // ─── 新規登録ダイアログ（即払い非利用ワーカーの事前登録） ─────────────
  const [isRegisterOpen, setIsRegisterOpen] = useState<boolean>(false);
  const [registerForm, setRegisterForm] = useState(EMPTY_WORKER_FORM);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState<string | null>(null);

  const [selectedTierCode, setSelectedTierCode] = useState<string | null>(searchParams.get('tier'));
  const [filterByAdvice, setFilterByAdvice] = useState<boolean>(searchParams.get('advice') === 'true');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // ─── ランクサマリ取得（tier別人数） ────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    if (!storeId) {
      setSummary(null);
      return;
    }
    try {
      const data = await crewApi.getCrewRanksSummary(storeId, token);
      setSummary(data);
      setError(null);
    } catch (err) {
      setSummary(null);
      setError(getApiErrorMessage(err, 'ランクサマリの取得に失敗しました'));
    }
  }, [storeId, token]);

  // ─── クルー一覧取得（応募リストを store_user 単位に集約） ────────────────
  const fetchCrews = useCallback(async () => {
    if (!storeId) {
      setCrews([]);
      return;
    }
    try {
      const [applications, adviceList, repliedOffers] = await Promise.all([
        applicationApi.getAll({}, storeId) as Promise<JobApplication[]>,
        crewApi.getCopilotAdvice(storeId, token, { status: 'active' }).catch(() => []),
        offersApi.getRepliedSpecialOffers(storeId, token).catch(() => []),
      ]);
      // active advice を store_user_id 単位で集計
      const adviceCountMap = new Map<string, number>();
      adviceList.forEach((a) => {
        if (a.store_user_id) {
          adviceCountMap.set(a.store_user_id, (adviceCountMap.get(a.store_user_id) ?? 0) + 1);
        }
      });
      // 返信済み（承諾/見送り）特別オファーを store_user_id 単位で集計
      const repliedOfferCountMap = new Map<string, number>();
      repliedOffers.forEach((o) => {
        if (o.store_user_id) {
          repliedOfferCountMap.set(o.store_user_id, (repliedOfferCountMap.get(o.store_user_id) ?? 0) + 1);
        }
      });
      const byStoreUser = new Map<string, CrewListItem>();
      applications
        // 応募がある＝採用。rejected/cancelled/no_show 以外の応募者をクルーとして表示する。
        .filter((app) => CREW_APPLICATION_STATUSES.includes(app.status ?? 'submitted'))
        .forEach((app) => {
          const sid = app.store_user_id ? String(app.store_user_id) : '';
          if (!sid) return;
          const status = app.status ?? 'submitted';
          const isAccepted = status === 'accepted';
          const existing = byStoreUser.get(sid);
          if (!existing) {
            byStoreUser.set(sid, {
              storeUserId: sid,
              // クルー詳細と同じ「現在の登録氏名」を優先表示する（応募時スナップショットの
              // applicant_name は名前変更に追従しないため）。未提供時のみ従来値へフォールバック。
              name: app.store_user_name || app.applicant_name,
              tierCode: app.tier_code ?? null,
              activeAdviceCount: adviceCountMap.get(sid) ?? (app.active_advice_count ?? 0),
              repliedOfferCount: repliedOfferCountMap.get(sid) ?? 0,
            });
          } else if (isAccepted && existing.isPendingApplicant) {
            // 同一 store_user に採用確定応募が1件でもあれば「採用確定」に昇格（詳細遷移可・rank 表示可）
            existing.isPendingApplicant = false;
            existing.applicationStatus = 'accepted';
            existing.tierCode = app.tier_code ?? existing.tierCode ?? null;
          }
        });
      setCrews(Array.from(byStoreUser.values()));
    } catch (err) {
      setCrews([]);
      setError((prev) => prev ?? getApiErrorMessage(err, 'クルー一覧の取得に失敗しました'));
    }
  }, [storeId, token]);

  // ─── 管理画面登録ワーカー一覧取得（応募を持たないためクルー一覧にマージする） ──
  const fetchAdminWorkers = useCallback(async () => {
    if (!storeId) {
      setAdminWorkers([]);
      return;
    }
    try {
      const items = await adminWorkerApi.list(storeId, token);
      setAdminWorkers(items);
    } catch {
      // 一覧の主データではないため、失敗しても画面全体はエラーにしない
      setAdminWorkers([]);
    }
  }, [storeId, token]);

  // ─── 即払い利用状態の取得（切替Switchの表示用） ─────────────────────
  const fetchSokyuStatus = useCallback(async () => {
    if (!storeId) {
      setSokyuStatusMap(new Map());
      return;
    }
    try {
      const items = await adminWorkerApi.sokyuStatus(storeId, token);
      setSokyuStatusMap(new Map(items.map((s) => [s.store_user_id, s])));
    } catch {
      // 切替UIの付加情報のため、失敗しても画面全体はエラーにしない
      setSokyuStatusMap(new Map());
    }
  }, [storeId, token]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchSummary(), fetchCrews(), fetchAdminWorkers(), fetchSokyuStatus(),
      ]);
      setIsLoading(false);
    };
    load();
  }, [fetchSummary, fetchCrews, fetchAdminWorkers, fetchSokyuStatus]);

  // ─── 即払い切替の実行（確認ダイアログの「切り替える」押下時） ────────────
  const handleToggleConfirm = async () => {
    if (!storeId || !toggleTarget) return;
    setIsToggling(true);
    setToggleError(null);
    try {
      const res = await adminWorkerApi.updateSokyuOptOut(
        toggleTarget.status.user_id,
        storeId,
        toggleTarget.nextOptOut,
        token
      );
      setSokyuStatusMap((prev) => {
        const next = new Map(prev);
        next.set(toggleTarget.status.store_user_id, {
          ...toggleTarget.status,
          sokyu_opt_out: res.sokyu_opt_out,
        });
        return next;
      });
      setToggleSuccess(
        toggleTarget.nextOptOut
          ? `${toggleTarget.status.name} さんの即払いを停止しました。以後の給与は店舗からの給与支払いになります。`
          : `${toggleTarget.status.name} さんの即払いを再開しました。本人がアプリで口座登録を行うと利用可能になります。`
      );
      setToggleTarget(null);
    } catch (err) {
      setToggleError(getApiErrorMessage(err, '即払いの切替に失敗しました'));
    } finally {
      setIsToggling(false);
    }
  };

  // ─── 未連携の事前登録クルーの削除（確認ダイアログの「削除する」押下時） ───
  const handleDeleteConfirm = async () => {
    if (!storeId || !deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await adminWorkerApi.deleteUnlinked(deleteTarget.storeUserId, storeId, token);
      setDeleteSuccess(`${deleteTarget.name} さんの事前登録を削除しました。`);
      setDeleteTarget(null);
      await Promise.all([fetchAdminWorkers(), fetchSokyuStatus()]);
    } catch (err) {
      setDeleteError(getApiErrorMessage(err, 'クルーの削除に失敗しました'));
    } finally {
      setIsDeleting(false);
    }
  };

  // ─── 既定時給の保存（編集ダイアログの「保存」押下時） ─────────────────
  const openWageDialog = (status: StoreWorkerSokyuStatusItem) => {
    setWageError(null);
    setWageTarget(status);
    setWageInput(status.default_hourly_wage != null ? String(status.default_hourly_wage) : '');
  };

  const handleSaveWage = async () => {
    if (!storeId || !wageTarget) return;
    const raw = wageInput.trim();
    // 空 → null（未設定に戻す＝承認時手入力へ）。入力ありは正の数のみ。
    const wageNum = raw === '' ? null : Number(raw);
    if (wageNum != null && (!Number.isFinite(wageNum) || wageNum <= 0)) {
      setWageError('時給は正の数で入力してください（未設定に戻す場合は空欄）');
      return;
    }
    setIsSavingWage(true);
    setWageError(null);
    try {
      const res = await adminWorkerApi.updateDefaultHourlyWage(
        wageTarget.user_id,
        storeId,
        wageNum,
        token
      );
      setSokyuStatusMap((prev) => {
        const next = new Map(prev);
        next.set(wageTarget.store_user_id, {
          ...wageTarget,
          default_hourly_wage: res.default_hourly_wage,
        });
        return next;
      });
      setWageTarget(null);
    } catch (err) {
      setWageError(getApiErrorMessage(err, '時給の保存に失敗しました'));
    } finally {
      setIsSavingWage(false);
    }
  };

  // ─── 新規登録ダイアログの送信 ──────────────────────────────────────
  const handleRegisterSubmit = async () => {
    if (!storeId) return;
    setIsRegistering(true);
    setRegisterError(null);
    try {
      const wageRaw = registerForm.default_hourly_wage.trim();
      const wageNum = wageRaw === '' ? null : Number(wageRaw);
      const payload: AdminWorkerCreatePayload = {
        last_name: registerForm.last_name.trim(),
        first_name: registerForm.first_name.trim(),
        birth_date: registerForm.birth_date,
        phone: registerForm.phone.trim(),
        email: registerForm.email.trim(),
        store_id: storeId,
        ...(registerForm.memo.trim() ? { memo: registerForm.memo.trim() } : {}),
        ...(wageNum != null && Number.isFinite(wageNum) && wageNum > 0
          ? { default_hourly_wage: wageNum }
          : {}),
      };
      await adminWorkerApi.create(payload, token);
      setIsRegisterOpen(false);
      setRegisterForm(EMPTY_WORKER_FORM);
      setRegisterSuccess(
        `${payload.last_name} ${payload.first_name} さんを即払い対象外のクルーとして登録しました。` +
        '本人が店舗の出勤QRコードからLINE登録すると連携されます（メールアドレス・電話番号・生年月日の一致が必要）。'
      );
      // sokyuStatusMap にも新規ワーカーを載せないと、リロードするまで
      // 行の即払い切替 Switch が表示されない
      await Promise.all([fetchAdminWorkers(), fetchSokyuStatus()]);
    } catch (err) {
      setRegisterError(getApiErrorMessage(err, 'クルーの登録に失敗しました'));
    } finally {
      setIsRegistering(false);
    }
  };

  // 16歳未満はサーバ側でも 400 になる。入力中に気づけるよう生年月日欄に出す
  // （未入力のうちはエラー表示しない）。
  const isUnderAge =
    registerForm.birth_date !== '' && !isOldEnough(registerForm.birth_date);

  const isRegisterFormValid =
    registerForm.last_name.trim() !== '' &&
    registerForm.first_name.trim() !== '' &&
    registerForm.birth_date !== '' &&
    !isUnderAge &&
    registerForm.phone.trim() !== '' &&
    /.+@.+\..+/.test(registerForm.email.trim());

  // ─── tier カードの表示順・件数（ホーム画面と共通のロジック・データソース） ──
  const orderedTiers = useCrewRankTiers(summary);

  // ─── 事前登録済みだが本人未連携のクルー数（③到達時のアラート用） ─────────
  // ワーカーが自己登録する際、メールアドレス・電話番号・生年月日の3点が一致すれば
  // この事前登録行に紐付くが、いずれかが違うと新規クルーとして作成され同一人物が
  // 2行に分かれる。未連携が残っている店舗に注意喚起する。
  const unlinkedPrelinkedCount = useMemo(
    () => adminWorkers.filter((w) => !w.line_linked).length,
    [adminWorkers]
  );

  // ─── 管理画面登録ワーカーをクルー一覧へマージ ─────────────────────────
  // 応募ベースの一覧に既に載っている（応募が発生した）ワーカーは重複させない。
  const combinedCrews = useMemo(() => {
    const existing = new Set(crews.map((c) => c.storeUserId));
    const adminRows: CrewListItem[] = adminWorkers
      .filter((w) => !existing.has(w.store_user_id))
      .map((w) => ({
        storeUserId: w.store_user_id,
        name: w.name,
        tierCode: null,
        activeAdviceCount: 0,
        isAdminRegistered: true,
        lineLinked: w.line_linked,
      }));
    return [...crews, ...adminRows];
  }, [crews, adminWorkers]);

  // ─── 一覧のフィルタリング（tier + 育成アドバイス + 検索） ──────────────
  const filteredCrews = useMemo(() => {
    return combinedCrews.filter((c) => {
      const matchesTier =
        !selectedTierCode ||
        c.tierCode === selectedTierCode;
      const matchesAdvice = !filterByAdvice || (c.activeAdviceCount ?? 0) > 0;
      const matchesSearch =
        searchQuery.trim() === '' ||
        c.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesTier && matchesAdvice && matchesSearch;
    });
  }, [combinedCrews, selectedTierCode, filterByAdvice, searchQuery]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="クルー管理"
        icon={
          <SvgIcon
            component={SidebarCrewMgmtIcon}
            inheritViewBox
            sx={{ color: '#898989', fontSize: 22 }}
          />
        }
        description={
          <Typography variant="body2" color="text.secondary">
            ランク別のクルー数を確認し、クルーをフィルタリングできます
          </Typography>
        }
        actionAlign="flex-start"
        action={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'flex-end',
              flexWrap: 'wrap',
              marginRight: 1,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={() => navigate('/crew-management/import')}
              disabled={storeId === null}
              sx={{ borderRadius: '8px', textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              CSV一括登録
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddAlt1Icon />}
              onClick={() => { setRegisterError(null); setIsRegisterOpen(true); }}
              disabled={storeId === null}
              sx={{ bgcolor: PRIMARY_BLUE, borderRadius: '8px', textTransform: 'none', boxShadow: 'none', whiteSpace: 'nowrap' }}
            >
              クルー新規登録
            </Button>
          </Box>
        }
      />

      {storeId === null && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          店舗IDが設定されていません。店舗情報を設定してください。
        </Alert>
      )}

      {registerSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRegisterSuccess(null)}>
          {registerSuccess}
        </Alert>
      )}

      {unlinkedPrelinkedCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          本人のLINE連携が未完了の事前登録クルーが {unlinkedPrelinkedCount} 名います。
          連携にはメールアドレス・電話番号・生年月日の3点が一致する必要があります。
          本人が別の情報で登録すると同じ人が2行に分かれて表示されるため、
          「未連携」のクルーが残っている場合は登録内容をご確認ください。
        </Alert>
      )}

      {toggleSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setToggleSuccess(null)}>
          {toggleSuccess}
        </Alert>
      )}

      {deleteSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDeleteSuccess(null)}>
          {deleteSuccess}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ランク別人数カード（クリックでフィルタ、再クリックで解除） */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${orderedTiers.length + 1}, minmax(0, 1fr))`,
          gap: 1,
          mb: 2,
        }}
      >
        {orderedTiers.map((t) => {
          const isSelected = selectedTierCode === t.code;
          return (
            <Paper
              key={t.code}
              onClick={() => { setSelectedTierCode((prev) => (prev === t.code ? null : t.code)); setFilterByAdvice(false); }}
              sx={{
                px: 1,
                py: 1.25,
                borderRadius: '12px',
                boxShadow: 'none',
                border: isSelected ? `2px solid ${PRIMARY_BLUE}` : '1px solid #e7e7e7',
                bgcolor: isSelected ? '#f0f7ff' : '#fff',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'border .15s, background-color .15s',
                '&:hover': { bgcolor: isSelected ? '#e8f2ff' : '#f8f8f8' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.25 }}>
                <RankStar tierCode={t.code} color={t.tier?.color} size={22} />
              </Box>
              <Typography variant="caption" sx={{ display: 'block', color: '#898989', fontWeight: 600, fontSize: '0.45rem', mb: 0.25, minHeight: '1.5rem' }}>
                {t.tier?.name ?? getRankTierLabel(t.code)}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: PRIMARY_BLUE, lineHeight: 1 }}>
                {t.count}
              </Typography>
            </Paper>
          );
        })}

        {/* 育成アドバイスカード */}
        <Paper
          onClick={() => { setFilterByAdvice((prev) => !prev); setSelectedTierCode(null); }}
          sx={{
            px: 0,
            py: 1.25,
            borderRadius: '12px',
            boxShadow: 'none',
            border: filterByAdvice ? `2px solid ${ADVICE_BADGE_PURPLE}` : '1px solid #673FC855',
            bgcolor: filterByAdvice ? '#f5eeff' : '#fff',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border .15s, background-color .15s',
            '&:hover': { bgcolor: filterByAdvice ? '#ede5ff' : '#f8f0ff' },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.25 }}>
            <AutoAwesomeIcon sx={{ color: ADVICE_BADGE_PURPLE, fontSize: 22 }} />
          </Box>
          <Typography variant="caption" sx={{ display: 'block', color: '#898989', fontWeight: 600, fontSize: '0.45rem', mb: 0.25, whiteSpace: 'pre-line', minHeight: '1.5rem' }}>
            {"育成\nアドバイス"}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: ADVICE_BADGE_PURPLE, lineHeight: 1 }}>
            {summary?.advice_count ?? 0}
          </Typography>
        </Paper>
      </Box>

      {/* 検索 */}
      <TextField
        placeholder="クルーを検索..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* クルー一覧 */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : storeId === null ? (
        <Paper sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid #e7e7e7', textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary">
            店舗IDが設定されていないため、データを取得できません
          </Typography>
        </Paper>
      ) : filteredCrews.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <SearchOffIcon sx={{ fontSize: 36, mb: 1, color: '#c4c4c4' }} />
          <Typography variant="body1" color="text.secondary">
            該当するクルーがいません
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {filteredCrews.map((crew) => {
            const sokyu = sokyuStatusMap.get(crew.storeUserId);
            // 本人未連携の事前登録行 / 未採用の応募者は詳細バンドル（rank）を持たないため詳細遷移しない。
            // 本人連携済み（line_linked）の事前登録クルーは飛び込み出勤で実績・ランクを持ち得るので
            // 遷移可にする（users.registration_source='admin' は本人連携後も維持されるため、
            // 管理画面登録ワーカー一覧に載っていることだけでは詳細不可の根拠にならない）。
            const nonNavigable =
              (crew.isAdminRegistered && !crew.lineLinked) || crew.isPendingApplicant;
            return (
            <Box
              key={crew.storeUserId}
              onClick={nonNavigable ? undefined : () => navigate(`/crew-management/${crew.storeUserId}`)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.75,
                borderRadius: '12px',
                bgcolor: '#fff',
                border: '1px solid #cfe0f5',
                cursor: nonNavigable ? 'default' : 'pointer',
                transition: 'background-color .15s',
                '&:hover': { bgcolor: nonNavigable ? '#fff' : '#f5f9ff' },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: PRIMARY_BLUE,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {crew.name} さん
              </Typography>
              <Box sx={{ flexShrink: 0, ml: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* 未採用（応募中/審査中）の応募者。採用操作なしでも一覧に出す運用のためステータスで区別する */}
                {crew.isPendingApplicant && (
                  <Chip
                    label={PENDING_STATUS_LABEL[crew.applicationStatus ?? 'submitted'] ?? '応募中'}
                    size="small"
                    sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#e3f2fd', color: '#1565c0' }}
                  />
                )}
                {/* 即払いSwitchのOFFが対象外を表すため、Switch表示時はチップを出さない
                    （スマホ幅で名前が数文字に潰れる問題への対応）。
                    即払い状態を取得できずSwitchを出せない場合のみフォールバック表示 */}
                {!sokyu && crew.isAdminRegistered && (
                  <Chip
                    label="即払い対象外"
                    size="small"
                    sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#f0f0f0', color: '#666' }}
                  />
                )}
                {/* 課題116: 名寄せ失敗時の手動紐付けは廃止。未連携の状態表示のみ残す
                    （連携は本人のLINE登録時の名寄せ＝email/電話/生年月日 で行う） */}
                {crew.isAdminRegistered && !crew.lineLinked && (
                  <>
                    <Chip
                      label="LINE未連携"
                      size="small"
                      sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#fff4e0', color: '#b26a00' }}
                    />
                    {/* 本人が別の情報で自己登録して二重登録になった場合の掃除。
                        連携済みの行には出さない（勤怠・応募ごと消えるため BE でも 409） */}
                    <Tooltip title="事前登録を削除">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteError(null);
                          setDeleteTarget(crew);
                        }}
                        sx={{ p: 0.25, color: '#b0b0b0', '&:hover': { color: '#d32f2f' } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                {/* 既定時給（飛び込み出勤に自動適用）。即払い非利用（opt_out）のワーカーにのみ表示・編集。
                    即払い利用中はシフト時給を使うため既定時給は無関係。行クリックの詳細遷移と分離する */}
                {sokyu && sokyu.sokyu_opt_out && (
                  <Chip
                    label={sokyu.default_hourly_wage != null ? `時給 ¥${sokyu.default_hourly_wage.toLocaleString()}` : '時給 未設定'}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); openWageDialog(sokyu); }}
                    sx={{
                      fontSize: '0.65rem',
                      height: 20,
                      cursor: 'pointer',
                      bgcolor: sokyu.default_hourly_wage != null ? '#e8f5e9' : '#fff3e0',
                      color: sokyu.default_hourly_wage != null ? '#2e7d32' : '#c65f00',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                )}
                {/* 即払い利用切替（docs/即給利用切替_設計書.md §5）。行クリックの詳細遷移と分離する */}
                {sokyu && (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'default' }}
                  >
                    <Typography variant="caption" sx={{ color: '#898989', fontSize: '0.65rem' }}>
                      即払い
                    </Typography>
                    <Switch
                      size="small"
                      checked={!sokyu.sokyu_opt_out}
                      disabled={isToggling}
                      onChange={() => {
                        setToggleError(null);
                        setToggleTarget({ status: sokyu, nextOptOut: !sokyu.sokyu_opt_out });
                      }}
                      inputProps={{ 'aria-label': `${sokyu.name} さんの即払い利用切替` }}
                    />
                  </Box>
                )}
                {(crew.activeAdviceCount ?? 0) > 0 && (
                  <>
                    <AutoAwesomeIcon sx={{ color: ADVICE_BADGE_PURPLE, fontSize: 16 }} />
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: ADVICE_BADGE_GRADIENT,
                        color: '#fff',
                        borderRadius: '50%',
                        minWidth: 20,
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        px: 0.5,
                      }}
                    >
                      {crew.activeAdviceCount}
                    </Box>
                  </>
                )}
                {/* 課題125: 返信済み（承諾/見送り）特別オファーがあればオレンジの通知バッジを出す */}
                {(crew.repliedOfferCount ?? 0) > 0 && (
                  <Box
                    aria-label={`返信済みの特別オファー ${crew.repliedOfferCount} 件`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: OFFER_REPLY_BADGE_ORANGE,
                      color: '#fff',
                      borderRadius: '50%',
                      minWidth: 20,
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      px: 0.5,
                    }}
                  >
                    {crew.repliedOfferCount}
                  </Box>
                )}
                {crew.tierCode && <RankStar tierCode={crew.tierCode} size={18} />}
                {!nonNavigable && (
                  <Box sx={{ color: '#bbb', fontSize: 18, lineHeight: 1 }}>›</Box>
                )}
              </Box>
            </Box>
            );
          })}
        </Box>
      )}

      {/* クルー新規登録ダイアログ（即払い非利用ワーカーの事前登録） */}
      <Dialog
        open={isRegisterOpen}
        onClose={() => { if (!isRegistering) setIsRegisterOpen(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>クルー新規登録</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
            ここから登録したクルーは<strong>即払い（即給）対象外</strong>になります。
            <strong>メールアドレス・電話番号・生年月日</strong>は、本人が出勤QRコードから
            LINE登録する際の照合キー（3点すべて一致で連携）になるため、正確に入力してください。
            一致しない場合は別のクルーとして新規登録され、同じ人が2行に分かれて表示されます。
          </Alert>
          {registerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {registerError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              label="姓"
              value={registerForm.last_name}
              onChange={(e) => setRegisterForm((f) => ({ ...f, last_name: e.target.value }))}
              required
              fullWidth
              margin="dense"
            />
            <TextField
              label="名"
              value={registerForm.first_name}
              onChange={(e) => setRegisterForm((f) => ({ ...f, first_name: e.target.value }))}
              required
              fullWidth
              margin="dense"
            />
          </Box>
          <TextField
            label="生年月日"
            type="date"
            value={registerForm.birth_date}
            onChange={(e) => setRegisterForm((f) => ({ ...f, birth_date: e.target.value }))}
            required
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            error={isUnderAge}
            helperText={isUnderAge ? UNDER_AGE_MESSAGE : undefined}
          />
          <TextField
            label="電話番号"
            value={registerForm.phone}
            onChange={(e) => setRegisterForm((f) => ({ ...f, phone: e.target.value }))}
            required
            fullWidth
            margin="dense"
            placeholder="090-1234-5678"
          />
          <TextField
            label="メールアドレス"
            type="email"
            value={registerForm.email}
            onChange={(e) => setRegisterForm((f) => ({ ...f, email: e.target.value }))}
            required
            fullWidth
            margin="dense"
          />
          <TextField
            label="時給（円・任意）"
            type="number"
            value={registerForm.default_hourly_wage}
            onChange={(e) => setRegisterForm((f) => ({ ...f, default_hourly_wage: e.target.value }))}
            fullWidth
            margin="dense"
            inputProps={{ min: 1, step: 10 }}
            helperText="設定すると、この店舗での飛び込み出勤（シフトなし）の勤怠に自動適用され、承認時の時給入力が不要になります。未入力なら承認時に入力します。"
          />
          <TextField
            label="メモ（任意）"
            value={registerForm.memo}
            onChange={(e) => setRegisterForm((f) => ({ ...f, memo: e.target.value }))}
            fullWidth
            margin="dense"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setIsRegisterOpen(false)}
            disabled={isRegistering}
            sx={{ textTransform: 'none', color: '#666' }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleRegisterSubmit}
            disabled={!isRegisterFormValid || isRegistering}
            sx={{ bgcolor: PRIMARY_BLUE, textTransform: 'none', boxShadow: 'none' }}
          >
            {isRegistering ? '登録中…' : '登録する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 即払い切替の確認ダイアログ（docs/即給利用切替_設計書.md §5） */}
      <Dialog
        open={toggleTarget !== null}
        onClose={() => { if (!isToggling) setToggleTarget(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {toggleTarget?.nextOptOut ? '即払いを停止しますか？' : '即払いを再開しますか？'}
        </DialogTitle>
        <DialogContent>
          {toggleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {toggleError}
            </Alert>
          )}
          {toggleTarget && (
            toggleTarget.nextOptOut ? (
              <Typography variant="body2">
                {toggleTarget.status.name} さんの即払い（即給）を停止します。
                以後の給与
                {toggleTarget.status.wallet_balance > 0 && (
                  <>
                    （未引き出し残高 ¥
                    {toggleTarget.status.wallet_balance.toLocaleString('ja-JP')} を含む）
                  </>
                )}
                は即給ではなく店舗からの給与支払いになります。
              </Typography>
            ) : (
              <Typography variant="body2">
                {toggleTarget.status.name} さんの即払い（即給）を再開します。
                本人がアプリで口座登録を行うと利用可能になります。
              </Typography>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setToggleTarget(null)}
            disabled={isToggling}
            sx={{ textTransform: 'none', color: '#666' }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleToggleConfirm}
            disabled={isToggling}
            sx={{ bgcolor: PRIMARY_BLUE, textTransform: 'none', boxShadow: 'none' }}
          >
            {isToggling ? '切替中…' : toggleTarget?.nextOptOut ? '停止する' : '再開する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 未連携の事前登録クルーの削除確認（docs/ワーカー自己登録_名寄せ3段階_設計書.md §4） */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => { if (!isDeleting) setDeleteTarget(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>事前登録を削除しますか？</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          {deleteTarget && (
            <Typography variant="body2">
              {deleteTarget.name} さんの事前登録（LINE未連携）を削除します。
              本人が別のメールアドレス・電話番号・生年月日で登録して二重になっている場合の
              掃除にお使いください。<br />
              本人が既に自己登録しているクルーは削除されません。
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={isDeleting}
            sx={{ textTransform: 'none', color: '#666' }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {isDeleting ? '削除中…' : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 既定時給の編集ダイアログ（飛び込み出勤に自動適用される時給） */}
      <Dialog open={!!wageTarget} onClose={() => !isSavingWage && setWageTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>時給の設定</DialogTitle>
        <DialogContent>
          {wageTarget && (
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              {wageTarget.name} さんの、この店舗での既定時給を設定します。設定すると、シフト未応募の
              飛び込み出勤（QR出退勤）の勤怠に自動適用され、承認時の時給入力が不要になります。
            </Typography>
          )}
          <TextField
            label="時給（円）"
            type="number"
            value={wageInput}
            onChange={(e) => setWageInput(e.target.value)}
            fullWidth
            margin="dense"
            inputProps={{ min: 1, step: 10 }}
            error={wageInput.trim() !== '' && !(Number(wageInput) > 0)}
            helperText="空欄で保存すると未設定に戻り、承認時に手入力する運用になります。"
          />
          {wageError && (
            <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 1 }}>
              {wageError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setWageTarget(null)}
            disabled={isSavingWage}
            sx={{ textTransform: 'none', color: '#666' }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveWage}
            disabled={isSavingWage || (wageInput.trim() !== '' && !(Number(wageInput) > 0))}
            sx={{ bgcolor: PRIMARY_BLUE, textTransform: 'none', boxShadow: 'none' }}
          >
            {isSavingWage ? '保存中…' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrewManagement;
